import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { Component, computed, DestroyRef, inject, Signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AppService } from 'app/app.service';
import { DashboardOverviewService, DashboardProductFamily } from './dashboard.service';
import { TimeSeriesPoint } from 'common/chart/line-chart.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import { RecentlyUsedRevisionsDataSource } from './recently-used-revisions-data-source';
import { ChipComponent } from 'common/designsystem/chip/chip.component';
import { DescriptionComponent } from 'common/description/description.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table';
import { MatSortHeader } from '@angular/material/sort';
import { PagedTableComponent } from 'common/table/paged-table.component';
import { ProgressBarComponent } from 'common/common/progress-bar.component';
import { tap } from 'rxjs';
import { IntakeTypeChipComponent } from 'common/designsystem/chip/intake-type-chip.component';
import { AddProductComponent } from 'component/add/add-product.component';
import { MatDialog } from '@angular/material/dialog';
import { BillingStatusService } from 'common/billing/billing-status.service';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

@Component({
  imports: [
    RouterLink,
    DecimalPipe,
    NgClass,
    MatProgressSpinner,
    TranslateModule,
    CardHeaderComponent,
    ChipComponent,
    DatePipe,
    DescriptionComponent,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatSortHeader,
    PagedTableComponent,
    ProgressBarComponent,
    IntakeTypeChipComponent,
    ButtonDirective,
    IconDirective,
  ],
  templateUrl: 'dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
})
export class DashboardComponent {
  private readonly dashboardOverviewService = inject(DashboardOverviewService);
  private readonly appService = inject(AppService);
  private readonly dialog = inject(MatDialog);
  private readonly billingStatusService = inject(BillingStatusService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly recentlyUsedRevisionsDataSource = new RecentlyUsedRevisionsDataSource();

  protected readonly chartWidth = 2200;
  protected readonly chartHeight = 256;

  protected readonly currentUser = computed(() => this.appService.user());

  protected readonly dashboard = toSignal(this.dashboardOverviewService.getOverview(), {
    initialValue: null,
  });

  protected readonly chartTimeSeries: Signal<TimeSeriesPoint[] | undefined> = computed(() => {
    const dashboard = this.dashboard();
    if (!dashboard) {
      return undefined;
    }
    const series = dashboard.findingsOverTime;
    return series.map((item) => ({
      date: item.label,
      value: item.total,
    }));
  });

  public constructor() {
    this.route.queryParamMap
      .pipe(
        tap((urlQueryParamMap) => {
          this.recentlyUsedRevisionsDataSource.refreshFromQueryParams(urlQueryParamMap);
          this.billingStatusService.refresh();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected severityClass(severity: string | undefined): string {
    if (!severity) {
      return '';
    }
    switch (severity) {
      case 'Critical':
        return 'ul-badge -danger';
      case 'Major':
        return 'ul-badge -warning';
      case 'Moderate':
        return 'ul-badge -info';
      default:
        return 'ul-badge -black';
    }
  }

  protected metricDeltaClass(tone: 'danger' | 'success'): string {
    return tone === 'danger' ? 'ul-badge -danger--light' : 'ul-badge -success--light';
  }

  protected hasOpenFindings(productFamily: DashboardProductFamily): boolean {
    return productFamily.criticalFindings > 0 || productFamily.majorFindings > 0;
  }

  protected openRequirementIntake(id: number): void {
    this.router.navigate([id], { relativeTo: this.route.parent });
  }

  protected async createProduct() {
    await AddProductComponent.open(this.dialog, this.billingStatusService, {
      origin: 'dashboard',
      manufacturer: this.appService.manufacturer()!,
    });
  }

  protected addEnabled = computed(() => {
    return this.billingStatusService.status()?.isProductCreationPossible === true;
  });
}
